import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { botApi, type WalkthroughResult } from '../../bot/api'
import { selectionPayload, selectionStorage } from '../../bot/chatSelection'
import { walkthroughAutoplay } from '../../bot/walkthroughAutoplay'
import type { SavedDrawing } from './workbenchState'
import { drawingSpan, drawnEntries, walkthroughSpan, type DrawingSpan } from './walkthroughPeriod'
import { prepareWalkthroughDisplay } from './walkthroughDisplay'

/** How often to ask the server whether a queued run has finished. */
const POLL_INTERVAL_MS = 3000

/**
 * How long before the band stops implying "any second now". The run is NOT abandoned — polling
 * continues; only the copy changes.
 */
const SLOW_AFTER_MS = 120_000

const STATUS_PENDING = 0
const STATUS_RUNNING = 1
const STATUS_ERROR = 200
const STATUS_CANCELED = 201

/** Why the band is preparing, which is the only thing its copy differs on. */
export type WalkthroughStartReason = 'start' | 'rebuild'

export type WalkthroughPhase =
  | { kind: 'idle' }
  | { kind: 'preparing'; reason: WalkthroughStartReason; since: number; runStatus: number | null; progressPct: number }
  | { kind: 'error'; message: string; action?: 'billing' }

/** Everything the overlay needs about a finished run. */
export interface WalkthroughRun {
  btid: string
  /**
   * Bumped on every attach, including a re-run the server answered with the SAME row: the replay
   * is keyed by it, so a fresh attach starts clean (its window and autoplay are read once).
   */
  attachId: number
  symbol: string
  timeframe: string
  /**
   * start_date T00:00:00Z, unix seconds — the run's REAL range, padded by the server with
   * indicator warm-up before the drawings and a trail after them, rounded to whole UTC days. The
   * lighter of the two tinted sections, where its trades are fetched from, and the replay's end.
   */
  rangeStartTs: number
  /** end_date T23:59:59Z, unix seconds. See rangeStartTs. */
  rangeEndTs: number
  /** The window around the drawings — the stronger tint, and where the replay starts. unix s. */
  displayStartTs: number
  displayEndTs: number
  status: number
  /** The server attached to a run it already had (true) rather than building this one now. */
  reused: boolean
  /** Open at the start and play: the Auto-verify loop just accepted this run (walkthroughAutoplay). */
  autoplay: boolean
  progressBacktest: string | null
  progressView: string | null
  /** The code version this run validates, or null when it is known to be another version's. */
  codeId: number | null
  configInput: WalkthroughResult['configInput']
}

export interface UseWalkthroughOverlayOptions {
  bid: string
  /** False while the workbench is still restoring. */
  ready: boolean
  /** The canvas's symbol and timeframe — what a new run is made for. */
  symbol: string
  timeframe: string
  /** The active symbol's drawings, serialized live; null when the canvas cannot say (mid-switch). */
  getDrawings: () => SavedDrawing[] | null
  hasCode: boolean
  codeId: number | null
  /** A one-shot request to run (or re-attach) the walkthrough — `?wt=1`. */
  requested: boolean
  /** The request was taken; drop it. */
  onRequestHandled: () => void
  /** A run the user asked for is ready: show it. */
  onShow: () => void
}

export interface UseWalkthroughOverlayReturn {
  run: WalkthroughRun | null
  phase: WalkthroughPhase
  /** Preparing for long enough that the copy should say so. */
  slow: boolean
  /** Run a walkthrough over the drawings as they are now (the toolbar's Walkthrough toggle, Retry, the chat's ?wt=1). */
  runWalkthrough: () => void
  dismissError: () => void
}

/** unix seconds for the inclusive start of a 'YYYY-MM-DD' UTC day. */
function dayStartTs(date: string): number {
  return Math.floor(Date.parse(`${date}T00:00:00Z`) / 1000)
}

/** unix seconds for the inclusive end of a 'YYYY-MM-DD' UTC day. */
function dayEndTs(date: string): number {
  return Math.floor(Date.parse(`${date}T23:59:59Z`) / 1000)
}

function errorMessage(err: unknown): { message: string; action?: 'billing' } {
  const code = err && typeof err === 'object' && 'errorCode' in err
    ? String((err as { errorCode: unknown }).errorCode)
    : ''

  switch (code) {
    case 'INSUFFICIENT_CREDITS':
      return {
        message: "You've used all of your usage allowance, so the walkthrough can't be prepared.",
        action: 'billing',
      }
    case 'CODE_NOT_FOUND':
      return { message: 'This strategy has no generated code yet.' }
    case 'WALKTHROUGH_NO_DATA':
      return {
        message: 'There is no price history for the period you drew on, so there is nothing to replay.',
      }
    case 'WALKTHROUGH_TIMEFRAME_INVALID':
      return { message: `The walkthrough doesn't support this chart's timeframe.` }
    case 'WALKTHROUGH_SPAN_INVALID':
      return { message: "Couldn't work out a period from your drawings." }
    case 'WALKTHROUGH_ALREADY_STARTING':
      return { message: 'A walkthrough is already being prepared. Give it a moment and try again.' }
    case 'RATE_LIMITED':
      return { message: 'Too many requests just now — wait a moment and try again.' }
    default:
      return { message: "Couldn't prepare the walkthrough." }
  }
}

/** How far the engine has got through the run's range, 0..100. */
function pctOf(progressBacktest: string | null | undefined, rangeStartTs: number, rangeEndTs: number): number {
  if (!progressBacktest) return 0
  const ts = Math.floor(Date.parse(progressBacktest.replace(' ', 'T') + 'Z') / 1000)
  if (!Number.isFinite(ts)) return 0
  const total = rangeEndTs - rangeStartTs
  if (total <= 0) return 0
  return Math.max(0, Math.min(100, Math.round(((ts - rangeStartTs) / total) * 100)))
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * The walkthrough as a layer of the canvas: which run is on it, and getting a new one.
 *
 * There is no mode to enter any more. On load the bot's latest walkthrough is looked up (never
 * created) and, if there is one, attached — the band appears, and the user shows or hides it.
 * The toolbar's Walkthrough toggle and the chat's `?wt=1` run one over the drawings as they are now; the
 * server answers with the run it already has when that still covers them, so a re-run costs
 * nothing unless something material changed.
 *
 * The run on screen is never swapped out from under the user: when the drawings move on from
 * the period it was made for, or the code changes, it stays until a new run is asked for.
 */
export default function useWalkthroughOverlay({
  bid,
  ready,
  symbol,
  timeframe,
  getDrawings,
  hasCode,
  codeId,
  requested,
  onRequestHandled,
  onShow,
}: UseWalkthroughOverlayOptions): UseWalkthroughOverlayReturn {
  const [run, setRun] = useState<WalkthroughRun | null>(null)
  const [phase, setPhase] = useState<WalkthroughPhase>({ kind: 'idle' })
  const [slowFor, setSlowFor] = useState<number | null>(null)

  // Bumped whenever we stop caring about an in-flight request, so a late answer can never
  // resurrect a run the user has since replaced. Also bumped on unmount.
  const epochRef = useRef(0)
  const attachSeqRef = useRef(0)

  const latest = useRef({ getDrawings, onShow, onRequestHandled, symbol, timeframe, codeId, hasCode, run })
  useLayoutEffect(() => {
    latest.current = { getDrawings, onShow, onRequestHandled, symbol, timeframe, codeId, hasCode, run }
  })

  useEffect(() => () => {
    epochRef.current += 1
  }, [])

  /**
   * Wait out a queued run, then work out its window and put it on the canvas.
   *
   * Polls the shared btid-keyed progress endpoint — the same one and the same cadence the
   * Backtest tab uses. The window is only known once the run has finished: its end depends on
   * where the code actually traded (prepareWalkthroughDisplay).
   */
  const attach = useCallback(
    async (
      result: WalkthroughResult,
      span: DrawingSpan,
      entries: ReturnType<typeof drawnEntries>,
      epoch: number,
      opts: { autoplay: boolean; show: boolean; codeCurrent: boolean; reason: WalkthroughStartReason | null },
    ) => {
      const period = result.walkthrough?.period
      const bc = result.configInput?.backtest_config ?? {}
      const rangeStartTs = dayStartTs(period?.startDate ?? bc.start_date)
      const rangeEndTs = dayEndTs(period?.endDate ?? bc.end_date)
      const runSymbol = result.walkthrough?.symbol ?? bc.symbol ?? latest.current.symbol
      const runTimeframe = (result.walkthrough?.timeframe ?? bc.timeframe ?? latest.current.timeframe).toUpperCase()

      let status = result.status
      let progressBacktest = result.progressBacktest ?? null

      if (status === STATUS_PENDING || status === STATUS_RUNNING) {
        setPhase((prev) =>
          prev.kind === 'preparing'
            ? prev
            : { kind: 'preparing', reason: opts.reason ?? 'start', since: Date.now(), runStatus: status, progressPct: 0 },
        )
        for (;;) {
          if (epoch !== epochRef.current) return
          try {
            const progress = await botApi.getBacktestProgress(result.btid)
            if (epoch !== epochRef.current) return
            status = progress.status
            progressBacktest = progress.progressBacktest ?? null
            if (status !== STATUS_PENDING && status !== STATUS_RUNNING) break
            const pct = pctOf(progressBacktest, rangeStartTs, rangeEndTs)
            setPhase((prev) => (prev.kind === 'preparing' ? { ...prev, runStatus: status, progressPct: pct } : prev))
          } catch {
            // A transient network blip must not kill a run that is still going; keep polling.
          }
          await sleep(POLL_INTERVAL_MS)
        }
      }

      if (status === STATUS_ERROR || status === STATUS_CANCELED) {
        if (epoch !== epochRef.current) return
        // Nothing to say when nobody asked: a failed run found on load just isn't shown.
        if (opts.reason === null) {
          setPhase({ kind: 'idle' })
          return
        }
        const results = await botApi.getBacktestResults(result.btid).catch(() => null)
        if (epoch !== epochRef.current) return
        setPhase({
          kind: 'error',
          message:
            (results as unknown as { error?: string } | null)?.error
            || result.resultSummary?.error
            || 'The walkthrough run failed before it produced any results.',
        })
        return
      }

      const display = await prepareWalkthroughDisplay({
        btid: result.btid,
        symbol: runSymbol,
        timeframe: runTimeframe,
        span,
        entries,
        rangeStartTs,
        rangeEndTs,
      })
      if (epoch !== epochRef.current) return

      attachSeqRef.current += 1
      setRun({
        btid: result.btid,
        attachId: attachSeqRef.current,
        symbol: runSymbol,
        timeframe: runTimeframe,
        rangeStartTs,
        rangeEndTs,
        displayStartTs: display.startTs,
        displayEndTs: display.endTs,
        status,
        // Absent means "not told", and the safe reading of that is the quiet one: a run we may
        // have been holding all along, which opens paused.
        reused: result.reused ?? true,
        autoplay: opts.autoplay,
        progressBacktest,
        progressView: result.progressView ?? null,
        codeId: opts.codeCurrent ? latest.current.codeId : null,
        configInput: result.configInput,
      })
      setPhase({ kind: 'idle' })
      if (opts.show) latest.current.onShow()
    },
    [],
  )

  // ---- A new run, over the drawings as they are now ----------------------------------------
  const runWalkthrough = useCallback(() => {
    const { symbol: sym, timeframe: tf, hasCode: code } = latest.current
    const drawings = latest.current.getDrawings()
    if (!code || !sym || !drawings) return
    const request = walkthroughSpan(sym, tf, drawings)
    const span = drawingSpan(drawings)
    if (!request || !span) return

    const epoch = ++epochRef.current
    const reason: WalkthroughStartReason = latest.current.run ? 'rebuild' : 'start'
    setPhase({ kind: 'preparing', reason, since: Date.now(), runStatus: null, progressPct: 0 })
    // Taken here, as the request goes out, so it belongs to this run and no later one.
    const autoplay = walkthroughAutoplay.take(bid)

    // The walkthrough's one AI call generates a backtest configuration, so it honours the same
    // model the user picked for the chat. Read at call time: this hook has no reason to
    // re-render on it.
    botApi.startWalkthrough(bid, request, selectionPayload(selectionStorage.get()))
      .then((result) =>
        attach(result, span, drawnEntries(drawings), epoch, { autoplay, show: true, codeCurrent: true, reason }),
      )
      .catch((err) => {
        if (epoch !== epochRef.current) return
        const { message, action } = errorMessage(err)
        setPhase({ kind: 'error', message, action })
      })
  }, [bid, attach])

  // ---- On load: whatever the bot already has, never creating one -----------------------------
  const lookedUpRef = useRef(false)
  useEffect(() => {
    if (!ready || !symbol || !hasCode || lookedUpRef.current) return
    lookedUpRef.current = true
    const drawings = latest.current.getDrawings() ?? []
    const live = drawingSpan(drawings)
    const request = walkthroughSpan(symbol, timeframe, drawings)
    const epoch = ++epochRef.current
    botApi.getWalkthrough(bid, request ?? undefined)
      .then((result) => {
        // No run is `null`; anything without a btid is not a run either.
        if (!result?.btid || epoch !== epochRef.current) return
        // The window around the drawings is worked out from the drawings on the chart when the
        // run still covers them; otherwise from the span it was made for.
        const requestedSpan = result.walkthrough?.requested
        const covered = live != null && result.reusable !== false
        const span: DrawingSpan | null = covered
          ? live
          : requestedSpan
            ? { fromTs: requestedSpan.fromTs, toTs: requestedSpan.toTs, count: 1 }
            : live
        if (!span) return
        void attach(result, span, drawnEntries(drawings), epoch, {
          autoplay: false,
          show: false,
          codeCurrent: result.codeCurrent !== false,
          reason: null,
        })
      })
      .catch(() => {
        /* nothing to show; the toolbar can still start one */
      })
    // Once per mount: the bot has one current walkthrough, whatever symbol is on the canvas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, symbol, hasCode, bid])

  // ---- `?wt=1`: the chat's "View walkthrough", or the Auto-verify loop's finished run ---------
  // Held until the code is known as well as the chart: on a fresh load the parent learns
  // `hasCode` a beat after the canvas is ready, and taking the request before that would drop
  // it (there would be nothing to run yet). ?wt=1 is only ever set for a bot with code.
  useEffect(() => {
    if (!requested || !ready || !symbol || !hasCode) return
    latest.current.onRequestHandled()
    runWalkthrough()
  }, [requested, ready, symbol, hasCode, runWalkthrough])

  // ---- "Taking a while" ------------------------------------------------------------------
  // The user chose to never cap the drawn span, so a wide walkthrough can legitimately run for
  // minutes. Say so instead of looking stuck.
  const startedAt = phase.kind === 'preparing' ? phase.since : null
  useEffect(() => {
    if (startedAt == null) return undefined
    const remaining = Math.max(0, SLOW_AFTER_MS - (Date.now() - startedAt))
    const t = setTimeout(() => setSlowFor(startedAt), remaining)
    return () => clearTimeout(t)
  }, [startedAt])
  const slow = startedAt != null && slowFor === startedAt

  const dismissError = useCallback(() => {
    setPhase((prev) => (prev.kind === 'error' ? { kind: 'idle' } : prev))
  }, [])

  return { run, phase, slow, runWalkthrough, dismissError }
}
